
import React from "react";

import TextoInfoFarol from "../TextoInfoFarol";
import { idsFarois } from "constants/idsFarois";
import dayjs from "dayjs";
import { useTranslations } from "adapters/i18n";

export const constantsInfoFarol = (t) => ({
  labels: {
    cochoCreep: `${t("supplementation:creep-trough")}:`,
    cochoPadrao: `${t("supplementation:standard-trough")}:`,
    cochoCompartilhado: `${t("supplementation:shared-trough")}:`,
    nomeDoPasto: t("statusIndicator:pasture-name"),
    nomeDosPastos: t("statusIndicator:pasture-names"),
    data: t("common:date"),
    quantidadeDeAnimais: t("livestock:animals-amount"),
    estruturaDoCocho: t("supplementation:trough-structure"),
    acessoAoCocho: t("supplementation:trough-access"),
    cochoCoberto: t("statusIndicator:covered-trough"),
    formulacao: t("supplementation:formulation"),
    quantidade: t("common:amount"),
    bom: t("supplementation:quality.good"),
    ruim: t("supplementation:quality.bad"),
    cochoCobertoMsg: t("statusIndicator:trough-is-covered"),
    cochoNaoCobertoMsg: t("statusIndicator:trough-is-not-covered"),
    coleta: t("statusIndicator:measure"),
    desabastecimento: t("statusIndicator:shortage"),
  },
  adornmentCochoCor: {
    padrao: "#24e9de",
    compartilhado: "#7a3cdc",
  },
  dataFormat: "DD/MM/YYYY",
});



const mensagensFarolEstruturaEAcessoAoCocho = (labels) => ({
  [true]: labels.bom,
  [false]: labels.ruim,
});

const mensagensFarolCochoCoberto = (labels) => ({
  [true]: labels.cochoCobertoMsg,
  [false]: labels.cochoNaoCobertoMsg,
});

export const infoPorFarolSelecionado = (constants) => {
  const { labels, dataFormat } = constants;
  return {
    [idsFarois.cochoProduto]: (valor) => ({
      [labels.quantidadeDeAnimais]: [valor.QuantidadeAnimais || 0],
    }),
    [idsFarois.cochoEstrutura]: (valor) => ({
      [labels.estruturaDoCocho]: [
        mensagensFarolEstruturaEAcessoAoCocho(labels)[valor.Valor],
      ],
    }),
    [idsFarois.cochoAcesso]: (valor) => ({
      [labels.acessoAoCocho]: [
        mensagensFarolEstruturaEAcessoAoCocho(labels)[valor.Valor],
      ],
    }),
    [idsFarois.cochoCoberto]: (valor) => ({
      [labels.cochoCoberto]: [mensagensFarolCochoCoberto(labels)[valor.Valor]],
    }),
    [idsFarois.cochoAbastecido]: (valor) => ({
      [labels.quantidadeDeAnimais]: [valor.QuantidadeAnimais || 0],
      ...(valor.Formula && { [labels.formulacao]: [valor.Formula] }),
      ...(valor.Formula && { [labels.quantidade]: [valor.Quantidade] }),
    }),
    [idsFarois.desabastecimento]: (valor) => ({
      ...(valor.DataColeta && {
        [labels.coleta]: [dayjs(valor.DataColeta).format(dataFormat)],
      }),
      ...(valor.DataDesabastecimento && {
        [labels.desabastecimento]: [
          dayjs(valor.DataDesabastecimento).format(dataFormat),
        ],
      }),
    }),
  }
};

const commonPropertiesCochoCompartilhado = (pastosComCochoCompartilhado, tipoFarol, constants) => {
  const { labels, adornmentCochoCor } = constants;
  let title = labels.cochoPadrao;
  let nomesDosPastos;
  let adornmentColor = adornmentCochoCor.padrao;
  let labelNomePastos = labels.nomeDoPasto;

  if (tipoFarol === "cocho-creep" && Array.isArray(pastosComCochoCompartilhado)) {
    const hasCreep = pastosComCochoCompartilhado
      .some(pasto => pasto?.PastoCochos
        && Array.isArray(pasto.PastoCochos)
        && pasto.PastoCochos.some(cocho => cocho?.CochoCreep === true));
    if (hasCreep) {
      title = labels.cochoCreep;
    }
  }

  if (pastosComCochoCompartilhado && pastosComCochoCompartilhado.length > 0) {
    if (pastosComCochoCompartilhado.length > 1) {
      title = labels.cochoCompartilhado;
      adornmentColor = adornmentCochoCor.compartilhado;
      labelNomePastos = labels.nomeDosPastos;
    }
    nomesDosPastos = pastosComCochoCompartilhado.map((pasto) => pasto.Nome);
  }

  return { title, nomesDosPastos, adornmentColor, labelNomePastos };
};

/**
 * Constrói o objeto `properties` (título → valores[]) para um farol de cocho.
 * Centraliza a lógica usada tanto em MontaTextoTooltipCochoCompartilhado
 * quanto no TooltipPasto, evitando duplicação.
 */
export const obterPropertiesCocho = (
  farolPasto,
  idFarolSelecionado,
  commonProperties,
  constants
) => {
  const infoPorFarol = infoPorFarolSelecionado(constants);
  const { labels } = constants;
  const { nomesDosPastos, labelNomePastos } = commonProperties;

  let properties = {};

  if (nomesDosPastos) {
    properties[labelNomePastos] = nomesDosPastos;
  }

  if (farolPasto && farolPasto.DataDaColeta) {
    properties[labels.data] = [farolPasto.DataDaColeta];
  }

  if (farolPasto && farolPasto.Valor !== null && infoPorFarol[idFarolSelecionado]) {
    const info = infoPorFarol[idFarolSelecionado](farolPasto.Valor);
    properties = { ...properties, ...info };
  }

  return properties;
};

export const MontaTextoTooltipCochoCompartilhado = (
  pastosComCochoCompartilhado,
  farolPasto,
  idFarolSelecionado,
  tipoFarol
) => {
  const { t } = useTranslations();
  const constants = constantsInfoFarol(t);
  const commonProperties = commonPropertiesCochoCompartilhado(
    pastosComCochoCompartilhado,
    tipoFarol,
    constants
  );

  const { title, adornmentColor } = commonProperties;

  const properties = obterPropertiesCocho(
    farolPasto,
    idFarolSelecionado,
    commonProperties,
    constants
  );

  return () => (
    <TextoInfoFarol
      title={title}
      properties={properties}
      titleAdornmentColor={adornmentColor}
    />
  );
};


